import { useQuery } from '@tanstack/react-query';

import { fetchEventResults, fetchLatestResults } from './api';
import type { EventResults, LatestResult } from './types';

function getHttpStatus(err: unknown): number | null {
  const e: any = err;
  return e?.response?.status ?? e?.request?.status ?? null;
}

export const resultsQueryKeys = {
  latest: (sportIds: string, numEvents: number) =>
    ['results', 'latest', sportIds, numEvents] as const,
  event: (eventId: number | string, scheduleId?: number | string | null) =>
    ['results', 'event', scheduleId ?? 'no-schedule', eventId] as const,
};

export function useLatestResultsQuery(params: {
  sportIds: string;
  numEvents: number;
  enabled?: boolean;
}) {
  const { sportIds, numEvents, enabled } = params;
  const isEnabled = enabled ?? sportIds.trim() !== '';

  return useQuery<LatestResult[]>({
    queryKey: resultsQueryKeys.latest(sportIds, numEvents),
    queryFn: () => fetchLatestResults({ sport_id: sportIds, num_events: numEvents }),
    enabled: isEnabled,
  });
}

export function useEventResultsQuery(params: {
  eventId: number | string | null | undefined;
  scheduleId?: number | string | null;
  enabled?: boolean;
  /** Poll until data has video_id (e.g. when LIVE for mini TV). Pass number (ms) to refetch on that interval until data has video_id. */
  refetchInterval?: number | ((data: EventResults | undefined) => number | false);
}) {
  const { eventId, scheduleId, enabled, refetchInterval } = params;
  const isEnabled = enabled ?? eventId != null;

  return useQuery<EventResults>({
    queryKey: resultsQueryKeys.event(eventId ?? 'none', scheduleId),
    queryFn: async () => {
      if (eventId == null) return {} as EventResults;
      try {
        return await fetchEventResults({
          event_id: eventId,
          ...(scheduleId != null && String(scheduleId).trim() !== '' ? { schedule_id: scheduleId } : {}),
        });
      } catch (err) {
        // Results may legitimately not exist yet; the API returns 404 until they are published.
        // 410 = Gone (e.g. Football3D must use schedule-scoped endpoint).
        const status = getHttpStatus(err);
        if (status === 404) return {} as EventResults;
        if (status === 410) return {} as EventResults;
        throw err;
      }
    },
    enabled: isEnabled && eventId != null,
    refetchInterval:
      typeof refetchInterval === 'function'
        ? (query) => refetchInterval(query.state.data)
        : refetchInterval,
    // Avoid aggressive retries; 404/410 are handled in queryFn.
    retry: (failureCount, error) => {
      const status = getHttpStatus(error);
      if (status === 404 || status === 410) return false;
      return failureCount < 2;
    },
    retryDelay: (attemptIndex, error) => {
      const status = getHttpStatus(error);
      if (status === 404 || status === 410) return 0;
      return Math.min(1000 * 2 ** attemptIndex, 10_000);
    },
  });
}

